/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';

import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';

import {validate2FaToken} from '../../../util/requestSender';

import ValidateTwoFactor from './validateTwoFactor';

import {ActivateHandle} from '../../../util/interfaces';

import {getFingerprint} from '../../../util/utils';

interface Props {
    /** `code` is the 2FA code that was just validated — callers that must prove the
     *  second factor to a follow-up request (e.g. disabling 2FA) pass it along. */
    onSuccess: (user: Record<string, any>, code: string) => void;
    explanation?: string;
}

function ValidateTwoFactorController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {explanation, onSuccess} = props;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [active, setActive] = useState(false);
    const twoFactorID = useRef<string | null>(null);

    function onActivate(_twoFactorID: string | null) {
        twoFactorID.current = _twoFactorID;
        setActive(true);
    }

    function onClose() {
        setLoading(false);
        setError(null);
        setActive(false);
        twoFactorID.current = null;
    }

    function onSubmitCode(code: string) {
        if (code.length !== 6) {
            setError('De code moet uit 6 cijfers bestaan');
            return;
        }
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            validate2FaToken({
                data: {two_factor_id: twoFactorID.current, token: code, fp: fingerprint},
                callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        const {user} = data;
                        onClose();
                        onSuccess(user, code);
                    } else setError('De inlogcode is niet correct');
                },
                // `callback` fires on 2xx only — a rejected code comes back as 403
                // and without this the dialog would spin forever.
                errorCallback: () => {
                    setLoading(false);
                    setError('De inlogcode is niet correct');
                }
            });
        });
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <ValidateTwoFactor
            onSubmit={onSubmitCode}
            onClose={onClose}
            active={active}
            loading={loading}
            error={error}
            explanation={explanation}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(ValidateTwoFactorController);
